import { useState } from 'react';
import { usePortalAuth } from '@/contexts/PortalAuthContext';
import { portalApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User } from 'lucide-react';

// Staff view their own record and can correct basic contact info (phone). Name/email/role are
// managed by an admin (identity + login fields).
export default function PortalProfile() {
  const { staff, refresh } = usePortalAuth();
  const [phone, setPhone] = useState(staff?.phone || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await portalApi.patch('/staff/me', { phone: phone.trim() });
      toast.success('Contact info updated');
      refresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="portal-profile">
      <h1 className="font-heading text-2xl font-700 text-charcoal">My Profile</h1>

      <div className="bg-white rounded-2xl border border-border/40 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-saffron/10 text-saffron flex items-center justify-center font-heading text-lg font-700">
            {staff?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-heading font-700 text-charcoal">{staff?.name}</p>
            <p className="text-xs text-muted-ink">{staff?.role}</p>
          </div>
        </div>

        <Row label="Full name" value={staff?.name} />
        <Row label="Email" value={staff?.email} />
        <Row label="Role" value={staff?.role} />

        <div className="space-y-1.5 pt-2 border-t border-border/30">
          <Label className="flex items-center gap-1.5"><User size={14} /> Phone (you can edit this)</Label>
          <Input data-testid="portal-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 780-555-0000" />
        </div>
        <Button data-testid="portal-profile-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">
          {busy ? 'Saving…' : 'Save Contact Info'}
        </Button>
        <p className="text-xs text-muted-ink">Need to change your name, email or role? Ask an admin — they manage those fields.</p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-ink">{label}</span>
      <span className="text-charcoal font-medium">{value}</span>
    </div>
  );
}
